import { getCurrentFinancialDeckEntity } from "@/models/financialDecks";
import { formatDecimalPercent } from "@/utils/format";
import {
  isNumber,
  evaluteWithErrors as solve,
  toNumberOrNull,
} from "@/utils/math";
import { formatMoney } from "@/utils/moneyFormating";
import { Timestamp } from "firebase/firestore";
import { Getter, PrimitiveAtom, atom } from "jotai";
import { focusAtom } from "jotai-optics";
import { DollarAmount, MonthAmount, PercentAmount, YearAmount } from "../types";
import { totalEquityDollarAtom } from "./equity";
import { createFocusAtom, generalInputAtom } from "./inputHelpers";
import { modelAtom } from "./model";

export const loanTypeAtom = atom((get) => {
  const entity = getCurrentFinancialDeckEntity();
  const { data } = get(entity.getObserveEntityRecordAtom());
  return data?.loanType || "sba7a";
});

export const sidebarAccordionStateAtom = focusAtom(modelAtom, (optic) =>
  optic
    .prop("general")
    .prop("sidebarAccordionState")
    .valueOr<boolean | string[] | null>(true),
);

export const dismissedWelcomeVideoAtom = focusAtom(modelAtom, (optic) =>
  optic.prop("general").prop("dismissedWelcomeVideo").valueOr<boolean>(false),
) as PrimitiveAtom<boolean>;

export const isSb7aLoanTypeAtom = atom((get) => {
  return get(loanTypeAtom) === "sba7a";
});

export const isConventionalLoanTypeAtom = atom((get) => {
  return get(loanTypeAtom) === "conventional";
});

// purchase information
export const targetPurchasePriceAtom = generalInputAtom<DollarAmount>(
  "targetPurchasePrice",
  null,
);

export const cashFlowAtom = generalInputAtom<DollarAmount>("cashFlow", null);

export const purchaseMultipleAtom = atom((get) =>
  solve<PercentAmount>("targetPurchasePrice / cashFlow", {
    targetPurchasePrice: get(targetPurchasePriceAtom),
    cashFlow: get(cashFlowAtom),
  }),
);

// real estate
export const isRealEstateIncludedAtom = createFocusAtom<boolean>(
  "isRealEstateIncludedAtom",
  false,
);

export const realEstateIsDollarFormatAtom = createFocusAtom<boolean>(
  "realEstateIsDollarFormat",
  true,
);

export const realEstateValueInputDollarAtom = generalInputAtom<DollarAmount>(
  "realEstateDollarValue",
  500000,
);

export const realEstateDollarValueAtom = atom((get) => {
  const realEstateValueInputDollar = get(realEstateValueInputDollarAtom);
  return realEstateValueInputDollar;
});

export const realEstateBusinessValueInputDollarAtom =
  generalInputAtom<DollarAmount>("realEstateBusinessValue", 500000);

export const realEstateBusinessValueDollarAtom = atom((get) => {
  const realEstateDollarValue = get(realEstateDollarValueAtom);
  const targetPurchasePrice = get(targetPurchasePriceAtom);

  return Number(targetPurchasePrice) - Number(realEstateDollarValue);
});

export const realEstatePercentAtom = atom((get) => {
  const realEstateValue = get(realEstateDollarValueAtom);
  const targetPurchasePrice = get(targetPurchasePriceAtom);

  if (Number(targetPurchasePrice) === 0) return 0;
  return solve<PercentAmount>("a / b", {
    a: realEstateValue,
    b: targetPurchasePrice,
  });
});

export const realEstateBusinessPercentAtom = atom((get) => {
  const realEstateBusinessValue = get(realEstateBusinessValueDollarAtom);
  const targetPurchasePrice = get(targetPurchasePriceAtom);

  if (Number(targetPurchasePrice) === 0) return 0;
  return solve<PercentAmount>("a / b", {
    a: realEstateBusinessValue,
    b: targetPurchasePrice,
  });
});

export const isRealEstateErrorValueProvidedAtom = atom((get) => {
  return (
    !get(realEstateDollarValueAtom) || !get(realEstateBusinessValueDollarAtom)
  );
});

export const isRealEstateErrorDollarTotalExceedsPurchasePriceAtom = atom(
  (get) => {
    const total = solve<DollarAmount>("a + b", {
      a: get(realEstateDollarValueAtom),
      b: get(realEstateBusinessValueDollarAtom),
    });

    return (
      Number(total) > Number(get(targetPurchasePriceAtom)) &&
      get(isRealEstateIncludedAtom) &&
      get(realEstateIsDollarFormatAtom)
    );
  },
);

export const realEstateErrorMessageAtom = atom((get) => {
  // make dep rerender
  get(isRealEstateErrorDollarTotalExceedsPurchasePriceAtom);

  if (!get(isRealEstateIncludedAtom)) return null;

  if (get(isRealEstateErrorDollarTotalExceedsPurchasePriceAtom)) {
    return "Combined value of the Business and Real Estate does not equal Purchase Price.";
  }

  return null;
});

export const isRealEstateInErrorAtom = atom((get) => {
  return !!get(realEstateErrorMessageAtom);
});

// finance structure

export const isEquityInvestorsIncludedAtom = createFocusAtom<boolean>(
  "isEquityInvestorsIncludedAtom",
  false,
);

export const isSellerRollingOverEquityAtom = createFocusAtom<boolean>(
  "isSellerRollingOverEquity",
  false,
);

export const inputIsInvestorFundedSearchPhaseAtom = createFocusAtom<boolean>(
  "isInvestorFundedSearchPhase",
  false,
);

export const isInvestorFundedSearchPhaseAtom = atom((get) => {
  const isEquityInvestorsIncluded = get(isEquityInvestorsIncludedAtom);
  const inputIsInvestorFundedSearchPhase = get(
    inputIsInvestorFundedSearchPhaseAtom,
  );

  if (!isEquityInvestorsIncluded) return false;
  return inputIsInvestorFundedSearchPhase;
});

export const financeStructureIsDollarFormatAtom = createFocusAtom<boolean>(
  "financeStructureIsDollarFormat",
  false,
);

export const sbaLoanPercentAtom = generalInputAtom<PercentAmount>(
  "sbaLoanPercent",
  0.8,
);

export const equityPercentAtom = generalInputAtom<PercentAmount>(
  "equityPercent",
  0.1,
);

export const equityInvestorPercentAtom = generalInputAtom<PercentAmount>(
  "equityInvestorPercent",
  0,
);

export const equitySellerRollingOverPercentAtom =
  generalInputAtom<PercentAmount>("equitySellerRollingOverPercent", 0);

export const equitySearchPhaseCapitalPercentAtom =
  generalInputAtom<PercentAmount>("equitySearchPhaseCapitalPercent", 0);

export const sellersNotePercentAtom = generalInputAtom<PercentAmount>(
  "sellersNotePercent",
  0.1,
);

export const sbaLoanInputDollarAtom = generalInputAtom<DollarAmount>(
  "sbaLoanDollar",
  0,
);

export const equityInputDollarAtom = generalInputAtom<DollarAmount>(
  "equityDollar",
  0,
);

export const equityInvestorInputDollarAtom = generalInputAtom<DollarAmount>(
  "equityInvestorDollar",
  0,
);

export const equitySellerRollingOverInputDollarAtom =
  generalInputAtom<DollarAmount>("equitySellerRollingOverDollar", 0);

export const equitySearchPhaseCapitalInputDollarAtom =
  generalInputAtom<DollarAmount>("equitySearchPhaseCapitalDollar", 0);

export const equitySearchPhaseCapitalDollarAtom = atom((get) => {
  const val = get(equitySearchPhaseCapitalInputDollarAtom);
  if (!get(isInvestorFundedSearchPhaseAtom)) return 0;
  return val;
});

export const sellersNoteInputDollarAtom = generalInputAtom<DollarAmount>(
  "sellersNoteDollar",
  0,
);

export const financeStructureTotalPercentAtom = atom((get) => {
  return solve<PercentAmount>("a + b + c + d + e", {
    a: get(sbaLoanPercentAtom),
    b: get(sellersNotePercentAtom),
    c: get(equityPercentAtom),
    d: get(isEquityInvestorsIncludedAtom) ? get(equityInvestorPercentAtom) : 0,
    e: get(isSellerRollingOverEquityAtom)
      ? get(equitySellerRollingOverPercentAtom)
      : 0,
  });
});

export const financeStructureErrorPercentAtom = atom((get) => {
  const total = get(financeStructureTotalPercentAtom);

  if (get(financeStructureIsDollarFormatAtom)) return false;

  return isNumber(total) && total !== 1;
});

export const financeStructureTotalDollarAtom = atom((get) => {
  return solve<DollarAmount>("a + b + c + d + e", {
    a: get(sbaLoanInputDollarAtom),
    b: get(equityInputDollarAtom),
    c: get(sellersNoteInputDollarAtom),
    d: get(isEquityInvestorsIncludedAtom)
      ? get(equityInvestorInputDollarAtom)
      : 0,
    e: get(isSellerRollingOverEquityAtom)
      ? get(equitySellerRollingOverInputDollarAtom)
      : 0,
  });
});

export const financeStructureErrorDollarAtom = atom((get) => {
  const purchasePrice = get(targetPurchasePriceAtom);
  const total = get(financeStructureTotalDollarAtom);
  if (!get(financeStructureIsDollarFormatAtom)) return false;

  return isNumber(total) && total !== purchasePrice;
});

export const financeStructureErrorSbaNoGreaterThan90PercentAtom = atom(
  (get) => {
    if (get(financeStructureIsDollarFormatAtom)) return false;
    if (!get(isSb7aLoanTypeAtom)) return false;

    return Number(get(sbaLoanPercentAtom)) > 0.9;
  },
);

export const financeStructureErrorMessageAtom = atom((get) => {
  if (get(financeStructureErrorSbaNoGreaterThan90PercentAtom)) {
    return `SBA 7(a) loan percentage must be less than 90%`;
  }

  if (
    get(financeStructureErrorPercentAtom) &&
    get(financeStructureTotalPercentAtom)
  ) {
    return `Percentages must add up to 100%. Current percent total is ${formatDecimalPercent(
      get(financeStructureTotalPercentAtom),
    )}
      `;
  }

  if (get(financeStructureErrorDollarAtom)) {
    return `Total dollar amount must equal purchase price. Current total is ${formatMoney(
      Number(get(financeStructureTotalDollarAtom)) || 0,
    )}
      `;
  }

  return null;
});

export const isFinanceStructureInErrorAtom = atom((get) => {
  return !!get(financeStructureErrorMessageAtom);
});

export const sbaLoanDollarAtom = atom((get) =>
  get(financeStructureIsDollarFormatAtom)
    ? get(sbaLoanInputDollarAtom)
    : Number(get(targetPurchasePriceAtom)) * Number(get(sbaLoanPercentAtom)),
);

export const equityDollarAtom = atom((get) => {
  const a =
    Number(get(targetPurchasePriceAtom)) * Number(get(equityPercentAtom));

  return get(financeStructureIsDollarFormatAtom)
    ? get(equityInputDollarAtom)
    : a;
});

export const equityInvestorDollarAtom = atom((get) => {
  const isEquityInvestorsIncluded = get(isEquityInvestorsIncludedAtom);
  const equityInvestorInputDollar = get(equityInvestorInputDollarAtom);
  const financeStructureIsDollarFormat = get(
    financeStructureIsDollarFormatAtom,
  );

  const targetPurchasePrice = get(targetPurchasePriceAtom);
  const equityInvestorPercent = get(equityInvestorPercentAtom);

  if (!isEquityInvestorsIncluded) return 0;
  if (financeStructureIsDollarFormat) return equityInvestorInputDollar;

  return Number(targetPurchasePrice) * Number(equityInvestorPercent);
});

export const sellersNoteDollarAtom = atom((get) =>
  get(financeStructureIsDollarFormatAtom)
    ? get(sellersNoteInputDollarAtom)
    : Number(get(targetPurchasePriceAtom)) *
      Number(get(sellersNotePercentAtom)),
);

export const equitySellerRolloverDollarAtom = atom((get) => {
  const mode = get(financeStructureIsDollarFormatAtom);
  const isSellerRollingOverEquity = get(isSellerRollingOverEquityAtom);
  const equitySellerRollingOverInputDollar = get(
    equitySellerRollingOverInputDollarAtom,
  );
  const equitySellerRollingOverPercent = get(
    equitySellerRollingOverPercentAtom,
  );
  const targetPurchasePrice = get(targetPurchasePriceAtom);

  if (!isSellerRollingOverEquity) return 0;

  if (mode) return equitySellerRollingOverInputDollar;

  return solve<DollarAmount>("a * b", {
    a: equitySellerRollingOverPercent,
    b: targetPurchasePrice,
  });
});

export const isExternalEquityAtom = atom((get) => {
  return (
    get(isEquityInvestorsIncludedAtom) || get(isSellerRollingOverEquityAtom)
  );
});

// equity investors

export const equityInvestorPreferredEquityRateAtom =
  generalInputAtom<PercentAmount>("equityInvestorPreferredEquityRate", 0.1);

export const equityInvestorEquityStepUpAtom = generalInputAtom<PercentAmount>(
  "equityInvestorEquityStepUp",
  2,
);

export const equityInvestorStartPrincipalRepaymentsAtom =
  createFocusAtom<YearAmount>("equityInvestorStartPrincipalRepayments", 1);

export const inputEquityInvestorStartPreferredDividendsAtom =
  createFocusAtom<YearAmount>("equityInvestorStartPreferredDividends", 1);

export const equityInvestorStartPreferredDividendsAtom = atom((get) => {
  const input = get(inputEquityInvestorStartPreferredDividendsAtom);
  const isInvestorFunded = get(isExternalEquityAtom);
  if (!isInvestorFunded) return 0;
  return input;
});

export const inputEquityInvestorStartProfitDistributionsAtom =
  createFocusAtom<YearAmount>("equityInvestorStartProfitDistributions", 1);

export const equityInvestorStartProfitDistributionsAtom = atom((get) => {
  const input = get(inputEquityInvestorStartProfitDistributionsAtom);
  const isInvestorFunded = get(isExternalEquityAtom);
  if (!isInvestorFunded) return 0;
  return input;
});

export const inputIsEquityCalculationMethodUsingProjectCostAtom =
  createFocusAtom<boolean>("isEquityCalculationMethodUsingProjectCost", false);

export const isEquityCalculationMethodUsingProjectCostAtom = atom((get) => {
  const input = get(inputIsEquityCalculationMethodUsingProjectCostAtom);
  // if no external equity, always use equity method
  const isExternalEquity = get(isExternalEquityAtom);

  if (!isExternalEquity) return false;
  return input;
});

export const inputEquityPromoteSearchersCarryPercentAtom =
  generalInputAtom<PercentAmount>("equityPromoteSearchersCarryPercent", 0.3);

export const equityPromoteSearchersCarryPercentAtom = atom((get) => {
  if (!get(isExternalEquityAtom)) return 1;
  return get(inputEquityPromoteSearchersCarryPercentAtom);
});

type EquityTreatment = "preferred" | "common";
export const equityTreatmentSearchPhaseAtom = createFocusAtom<EquityTreatment>(
  "equityTreatmentSearchPhase",
  "preferred",
);

export const equityTreatmentSearchPhaseApplyStepUpAtom =
  createFocusAtom<boolean>("equityTreatmentSearchPhaseApplyStepup", false);

export const equityTreatmentAcquisitionInvestorsAtom =
  createFocusAtom<EquityTreatment>("equityAcquisitionInvestors", "preferred");

export const equityTreatmentAcquisitionInvestorsApplyStepUpAtom =
  createFocusAtom<boolean>(
    "equityTreatmentAcquisitionInvestorsApplyStepUp",
    false,
  );

export const inputEquityTreatmentSearchersCoInvestAtom =
  createFocusAtom<EquityTreatment>(
    "equityTreatmentSearchersCoInvest",
    "preferred",
  );

export const equityTreatmentSearchersCoInvestAtom = atom((get) => {
  //if (!get(isExternalEquityAtom)) return "common";

  return get(inputEquityTreatmentSearchersCoInvestAtom);
});

export const equityTreatmentSearchersCoInvestApplyStepUpAtom =
  createFocusAtom<boolean>(
    "equityTreatmentSearchersCoInvestApplyStepUp",
    false,
  );

export const equityTreatmentSellerEquityRolloverAtom =
  createFocusAtom<EquityTreatment>(
    "equityTreatmentSellerEquityRollover",
    "preferred",
  );

export const equityTreatmentSellerEquityRolloverApplyStepUpAtom =
  createFocusAtom<boolean>(
    "equityTreatmentSellerEquityRolloverApplyStepUp",
    false,
  );

// lender line of credit

export const isLineOfCreditIncludedAtom = createFocusAtom<boolean>(
  "isLineOfCreditIncludedAtom",
  false,
);

export const lineOfCreditInterestRateAtom = generalInputAtom<PercentAmount>(
  "lineOfCreditInterestRate",
  0.08,
);

export const lenderLineOfCreditTotalInputAmountAtom =
  generalInputAtom<DollarAmount>("lenderTotalAmount", 0);

export const lenderLineOfCreditTotalAmountAtom = atom((get) => {
  const isIncluded = get(isLineOfCreditIncludedAtom);
  const totalInput = get(lenderLineOfCreditTotalInputAmountAtom);

  if (!isIncluded) return null;
  return totalInput;
});

export const lenderLineOfCreditDrawdownYearAtom = createFocusAtom<YearAmount>(
  "lenderLineOfCreditDrawdownYear",
  1,
);

export const lenderLineOfCreditDrawdownAtom = generalInputAtom<DollarAmount>(
  "lenderDayOneDrawdown",
  null,
);

export const lineOfCreditErrorMessageAtom = atom((get) => {
  const total = get(lenderLineOfCreditTotalAmountAtom);
  const drawdown = get(lenderLineOfCreditDrawdownAtom);

  if (!get(isLineOfCreditIncludedAtom)) return null;
  if (!isNumber(total) || !isNumber(drawdown)) return null;

  if (Number(drawdown) > Number(total)) {
    return `Drawdown Amount exceeds Total Amount`;
  }

  return null;
});

export const lineOfCreditIsInErrorAtom = atom((get) => {
  return !!get(lineOfCreditErrorMessageAtom);
});

// deal expenses

export const qualityOfEarningsAtom = generalInputAtom<DollarAmount>(
  "qualityOfEarnings",
  20000,
);

export const legalExpensesAtom = generalInputAtom<DollarAmount>(
  "legalExpenses",
  40000,
);

export const otherDealExpensesAtom = generalInputAtom<DollarAmount>(
  "otherDealExpenses",
  0,
);

export const totalDealExpensesAtom = atom((get) => {
  return solve<DollarAmount>("a + b + c", {
    a: Number(get(qualityOfEarningsAtom)) || 0,
    b: Number(get(legalExpensesAtom)) || 0,
    c: Number(get(otherDealExpensesAtom)) || 0,
  });
});

// sba loan
export const sbaLoanInterestRateAtom = generalInputAtom<PercentAmount>(
  "sbaLoanInterestRate",
  0.115,
);

export const sbaLoanStartDateAtom = createFocusAtom<Timestamp>(
  "sbaLoanStartDateAtom",
  Timestamp.fromDate(new Date()),
);

export const sbaLoanConventionalTermLengthAtom = generalInputAtom<YearAmount>(
  "sbaLoanConventionalTermLength",
  7,
);

export const sbaLoanConventionalHasInterestOnlyPeriodAtom =
  createFocusAtom<boolean>("sbaLoanConventionalHasInterestOnlyPeriod", false);

export const sbaLoanConventionalInterestOnlyPeriodAtom =
  generalInputAtom<MonthAmount>("sbaLoanConventionalInterestOnlyPeriod", 6);

export const sbaLoanConventionalAmortScheduleAtom =
  generalInputAtom<YearAmount>("sbaLoanConventionalAmortScheduleAtom", 7);

export const sbaLoanConventionalTransactionFeeAtom =
  generalInputAtom<DollarAmount>("sbaLoanConventionalTransactionFee", 50000);

export const sbaLoanConventionalErrorTermLengthGreaterThanAmort = atom(
  (get) => {
    return (
      Number(get(sbaLoanConventionalTermLengthAtom)) >
      Number(get(sbaLoanConventionalAmortScheduleAtom))
    );
  },
);

export const sbaLoanErrorMessageAtom = atom((get) => {
  if (get(sbaLoanConventionalErrorTermLengthGreaterThanAmort)) {
    return `Term Length cannot be greater than Amortization Schedule`;
  }

  return null;
});

export const sbaLoanIsInErrorAtom = atom((get) => {
  return !!get(sbaLoanErrorMessageAtom);
});

// sellers note
export const sellersNoteInterestRateAtom = generalInputAtom<PercentAmount>(
  "sellersNoteInterestRate",
  0.07,
);
export const sellersNoteAmortizationScheduleAtom = generalInputAtom<YearAmount>(
  "sellersNoteAmortizationSchedule",
  5,
);

export const sellersNoteTermLengthAtom = generalInputAtom<YearAmount>(
  "sellersNoteTermLength",
  5,
);

export const sellersNoteStartDateAtom = createFocusAtom<Timestamp>(
  "sellersNoteStartDateAtom",
  Timestamp.fromDate(new Date()),
);

export const sellersNoteHasStandbyProvisionAtom = createFocusAtom<boolean>(
  "hasStandbyProvision",
  false,
);

export const sellersNoteStandbyPeriodAtom = generalInputAtom<YearAmount>(
  "sellersNoteStandbyPeriod",
  2,
);

export const sellersNoteStandbyPeriodRangeErrorAtom = atom((get) => {
  const val = Number(get(sellersNoteStandbyPeriodAtom));
  return val < 1 || val > Number(get(sellersNoteAmortizationScheduleAtom));
});

export const sellersNoteStandbyPeriodErrorMessageAtom = atom((get) => {
  if (get(sellersNoteStandbyPeriodRangeErrorAtom)) {
    return `Standby Period must be at least 1 year or longer.`;
  }

  return null;
});

export const sellersNoteStandbyIsFullStandbyAtom = createFocusAtom<boolean>(
  "sellersNoteStandbyIsFullStandby",
  true,
);

export const sellersNoteStandbyAccrueInterestInFullStandbyAtom =
  createFocusAtom<boolean>(
    "sellersNoteStandbyAccrueInterestInFullStandby",
    false,
  );

// company sale
export const companySaleIncludedAtom = createFocusAtom<boolean>(
  "isCompanySaleIncluded",
  false,
);

export const companySaleYearAtom = generalInputAtom<YearAmount>(
  "companySaleYear",
  10,
);

export const companySaleExitMultipleAtom = generalInputAtom<PercentAmount>(
  "companySaleExitMultiple",
  0,
);

// buyer's compensation
export const buyerCompBuyersSalaryAtom = generalInputAtom<DollarAmount>(
  "buyerCompBuyersSalary",
  125000,
);
export const buyerCompPayrollTaxesAtom = generalInputAtom<DollarAmount>(
  "buyerCompPayrollTaxes",
  20000,
);
export const buyerCompHealthBenefitsAtom = generalInputAtom<DollarAmount>(
  "buyerCompHealthBenefits",
  7500,
);

export const buyerCompTotalAtom = atom<DollarAmount>((get) =>
  solve<DollarAmount>("a + b + c", {
    a: get(buyerCompBuyersSalaryAtom) || 0,
    b: get(buyerCompPayrollTaxesAtom) || 0,
    c: get(buyerCompHealthBenefitsAtom) || 0,
  }),
);

// operational assumptions
export const companysTaxRateAtom = generalInputAtom<PercentAmount>(
  "buyerTaxRate",
  0.37,
);
export const buyerDeprAndAmortScheduleAtom = generalInputAtom<YearAmount>(
  "buyerDeprAndAmortSchedule",
  15,
);

export const buyerDeprAndAmortScheduleDealExpensesAtom =
  generalInputAtom<YearAmount>("buyerDeprAndAmortScheduleDealExpenses", 15);

export const buyerAnnualCapExAtom = generalInputAtom<DollarAmount>(
  "buyerAnnualCapEx",
  0,
);

// deal fees
export const isRollinDealExpensesAtom = createFocusAtom<boolean>(
  "isRollinDealExpenses",
  true,
);

export const isRollinLoanTransactionFeeAtom = createFocusAtom<boolean>(
  "isRollinLoanTransactionFee",
  true,
);

export const isRollinSBAGuarantyFeeAtom = createFocusAtom<boolean>(
  "isRollinSBAGuarantyFee",
  true,
);

export const loanTransactionFeeAtom = atom((get) => {
  if (!get(sbaLoanDollarAtom) || Number(get(sbaLoanDollarAtom)) < 0.01)
    return 0;

  if (get(isConventionalLoanTypeAtom))
    return get(sbaLoanConventionalTransactionFeeAtom);

  return get(isRealEstateIncludedAtom) ? 30000 : 20000;
});

export const sbaTotalLoanAmountWithoutGuarantyFeeAtom = atom((get) => {
  const sbaLoanDollar = get(sbaLoanDollarAtom)
    ? Number(get(sbaLoanDollarAtom))
    : 0;

  const loanTransactionFee = get(isRollinLoanTransactionFeeAtom)
    ? Number(get(loanTransactionFeeAtom))
    : 0;

  const dealExpenses = get(isRollinDealExpensesAtom)
    ? Number(get(totalDealExpensesAtom))
    : 0;

  const workingCapital =
    get(workingCapitalIncludedAtom) &&
    get(workingCapitalFundingSourceAtom) === "seniorDebt"
      ? Number(get(workingCapitalBuyerCashAtom)) || 0
      : 0;

  return sbaLoanDollar + loanTransactionFee + dealExpenses + workingCapital;
});

const calcGuarantyFee = (
  get: Getter,
  underMilPercent: number,
  overMillPercent: number,
) => {
  const sbaTotal = get(sbaTotalLoanAmountWithoutGuarantyFeeAtom);

  const upToOneMiltotalLoanAmount =
    sbaTotal < 1333333.33 ? sbaTotal : 1333333.33;

  const feeUpToOneMiltotalLoanAmount =
    upToOneMiltotalLoanAmount * 0.75 * underMilPercent;

  const overOneMilTotalLoanAmount =
    sbaTotal >= 1333333.33 ? sbaTotal - upToOneMiltotalLoanAmount : 0;

  const feeOverOneMilTotalLoanAmount =
    overOneMilTotalLoanAmount * 0.75 * overMillPercent;

  return feeUpToOneMiltotalLoanAmount + feeOverOneMilTotalLoanAmount;
};

export const sbaLoanSize1To2MilFeeAtom = atom((get) =>
  calcGuarantyFee(get, 0.0145, 0.017),
);
export const sbaLoanSizeOver2MilFeeAtom = atom((get) =>
  calcGuarantyFee(get, 0.035, 0.0375),
);

export const sbaGuarantyFeeAtom = atom((get) => {
  // Make sure rerun for close values
  get(sbaLoanDollarAtom);
  get(sbaLoanSize1To2MilFeeAtom);
  get(sbaLoanSizeOver2MilFeeAtom);

  if (get(isConventionalLoanTypeAtom)) return 0;
  if (!get(sbaLoanDollarAtom) || Number(get(sbaLoanDollarAtom)) < 0.01)
    return 0;

  const sbaTotalLoanAmountWithoutGuarantyFee = get(
    sbaTotalLoanAmountWithoutGuarantyFeeAtom,
  );
  const sbaLoanSize1To2MilFee = get(sbaLoanSize1To2MilFeeAtom);
  const sbaLoanSizeOver2MilFee = get(sbaLoanSizeOver2MilFeeAtom);

  return sbaTotalLoanAmountWithoutGuarantyFee < 2000000
    ? sbaLoanSize1To2MilFee
    : sbaLoanSizeOver2MilFee;
});

export const sbaLoanOptionalFees = atom((get) => {
  const loanTransactionFee = get(isRollinLoanTransactionFeeAtom)
    ? Number(get(loanTransactionFeeAtom)) || 0
    : 0;

  const sbaguarantyFee = get(isRollinSBAGuarantyFeeAtom)
    ? Number(get(sbaGuarantyFeeAtom)) || 0
    : 0;

  const dealExpenses = get(isRollinDealExpensesAtom)
    ? Number(get(totalDealExpensesAtom)) || 0
    : 0;

  return loanTransactionFee + sbaguarantyFee + dealExpenses;
});

export const sbaLoanTotalWithOptionalFeesAtom = atom((get) => {
  const fundingSource = get(workingCapitalFundingSourceAtom);
  const workingCapitalBuyerCash = Number(get(workingCapitalBuyerCashAtom) || 0);
  return (
    Number(get(sbaLoanDollarAtom) || 0) +
    get(sbaLoanOptionalFees) +
    (fundingSource === "seniorDebt" ? workingCapitalBuyerCash : 0)
  );
});

export const businessAcquisitionCostAtom = atom((get) => {
  const isRealEstateIncluded = get(isRealEstateIncludedAtom);
  const targetPurchasePrice = get(targetPurchasePriceAtom);
  const realEstateBusinessValueDollar = get(realEstateBusinessValueDollarAtom);

  return !isRealEstateIncluded
    ? targetPurchasePrice
    : realEstateBusinessValueDollar;
});

export const cashRequiredByBorrowerClosingDayAtom = atom((get) => {
  const equityDollar = Number(get(equityDollarAtom));
  const sbaGuarantyFee = !get(isRollinSBAGuarantyFeeAtom)
    ? Number(get(sbaGuarantyFeeAtom))
    : 0;
  const loanTransactionFee = !get(isRollinLoanTransactionFeeAtom)
    ? Number(get(loanTransactionFeeAtom))
    : 0;

  const equityInvestorDollar =
    toNumberOrNull(get(equityInvestorDollarAtom)) || 0;

  const capitalCashBuyer =
    toNumberOrNull(get(workingCapitalBuyerCashAtom)) || 0;

  return (
    equityDollar +
    sbaGuarantyFee +
    loanTransactionFee +
    equityInvestorDollar +
    capitalCashBuyer
  );
});

export const cashToSellerOnClosingDayAtom = atom((get) => {
  return Number(get(totalEquityDollarAtom)) + Number(get(sbaLoanDollarAtom));
});

export const individualInvestorReturnStartDollarAtom =
  generalInputAtom<DollarAmount>("individualInvestorReturnStartDollar", 100000);

export const workingCapitalSellerIncludedAtom = createFocusAtom<boolean>(
  "workingCapitalSellerIncluding",
  false,
);

export const workingCapitalIncludedAtom = createFocusAtom<boolean>(
  "workingCapitalIncluded",
  false,
);

export const inputWorkingCapitalBuyerCashAtom = generalInputAtom<DollarAmount>(
  "workingCapitalCashBuyer",
  0,
);

export const workingCapitalBuyerCashAtom = atom((get) => {
  const included = get(workingCapitalIncludedAtom);
  const cash = get(inputWorkingCapitalBuyerCashAtom);
  return included ? cash : 0;
});

type WorkingCaptialFundingSource = "equity" | "seniorDebt";
export const workingCapitalFundingSourceAtom =
  createFocusAtom<WorkingCaptialFundingSource>(
    "workingCapitalFundingSource",
    "seniorDebt",
  );

type WorkingCaptialEquityContributor = "searcher" | "investors";
export const workingCapitalEquityContributorAtom =
  createFocusAtom<WorkingCaptialEquityContributor>(
    "workingCapitalEquityContributor",
    "searcher",
  );

export const workingCapitalSellerCashAtom = generalInputAtom<DollarAmount>(
  "workingCapitalCashSeller",
  0,
);

export const workingCapitalSellerAccountsReceivableAtom =
  generalInputAtom<DollarAmount>("workingCapitalAccountsReceivable", 0);

export const workingCapitalSellerInventoryAtom = generalInputAtom<DollarAmount>(
  "workingCapitalInventory",
  0,
);

export const workingCapitalSellerPrepaidExpensesAtom =
  generalInputAtom<DollarAmount>("workingCapitalPrepaidExpenses", 0);

export const workingCapitalSellerOtherCurrentAssetsAtom =
  generalInputAtom<DollarAmount>("workingCapitalOtherCurrentAssets", 0);

export const workingCapitalTotalAtom = atom((get) => {
  const sellerIncluded = get(workingCapitalSellerIncludedAtom);

  return solve<DollarAmount>("a + b + c + d + e + f", {
    a: Number(get(workingCapitalBuyerCashAtom)) || 0,
    b: sellerIncluded ? Number(get(workingCapitalSellerCashAtom)) || 0 : 0,
    c:
      sellerIncluded &&
      Number(get(workingCapitalSellerAccountsReceivableAtom)) > 0
        ? Number(get(workingCapitalSellerAccountsReceivableAtom)) || 0
        : 0,
    d: sellerIncluded ? Number(get(workingCapitalSellerInventoryAtom)) || 0 : 0,
    e: sellerIncluded
      ? Number(get(workingCapitalSellerPrepaidExpensesAtom)) || 0
      : 0,
    f: sellerIncluded
      ? Number(get(workingCapitalSellerOtherCurrentAssetsAtom)) || 0
      : 0,
  });
});

export const equityDollarWithCapitalAtom = atom((get) => {
  const equityDollar = Number(get(equityDollarAtom));
  const workingCapitalIncluded = get(workingCapitalIncludedAtom);
  const workingCapitalFundingSource = get(workingCapitalFundingSourceAtom);
  const workingCapitalEquityContributor = get(
    workingCapitalEquityContributorAtom,
  );
  const workingCapitalBuyerCash = Number(get(workingCapitalBuyerCashAtom)) || 0;

  return workingCapitalIncluded &&
    workingCapitalFundingSource === "equity" &&
    workingCapitalEquityContributor === "searcher"
    ? Number(equityDollar) + workingCapitalBuyerCash
    : equityDollar;
});

type BusinessEntityType = "llc" | "scorp" | "ccorp";
export const businessEntityTypeAtom = createFocusAtom<BusinessEntityType>(
  "businessEntityType",
  "llc",
);

// includeTaxDistributionsInIRRAndMOICAtom is false if ccorp otherwise true
export const includeTaxDistributionsInIRRAndMOICAtom = atom(
  (get) => get(businessEntityTypeAtom) !== "ccorp",
);

export const isGrowthCapExIncludedAtom = createFocusAtom<boolean>(
  "isGrowthCapExIncluded",
  false,
);

type GrowthExpenseItem = {
  id: string;
  name: string;
  amount: number;
  start: number;
  duration: number;
};

export const growthExpenseItemsAtom = createFocusAtom<GrowthExpenseItem[]>(
  "growthExpenseItemsAtom",
  [],
);

export const includeInvestorPayoutInHistoricalAtom = createFocusAtom<boolean>(
  "includeInvestorPayoutInHistorical",
  true,
);

export const includeSellerNoteInHistoricalAtom = createFocusAtom<boolean>(
  "includeSellerNoteInHistorical",
  true,
);
